import { useState, useEffect } from "react";
import axios from "axios";
import { ArrowRight, Check, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { fireLead, getAttribution } from "@/lib/analytics";
import FeaturedBrandsStrip from "@/components/landing/FeaturedBrandsStrip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRAND_CATEGORIES = [
  "Beauty",
  "Grocery",
  "Outdoor",
  "Wellness",
  "Professional Services",
];

export default function BetaSignup() {
  const [form, setForm] = useState({
    brand_name: "",
    email: "",
    website: "",
    category: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [brandCount, setBrandCount] = useState(null);
  const [verifiedCount, setVerifiedCount] = useState("700+");

  useEffect(() => {
    axios
      .get(`${API}/brand/count`)
      .then((r) => setBrandCount(r.data?.count ?? null))
      .catch(() => setBrandCount(null));
    axios
      .get(`${API}/stats/site`)
      .then((r) => {
        const v = (r.data?.verified_brand_count || "").trim();
        if (v) setVerifiedCount(v);
      })
      .catch(() => {
        /* leave the fallback */
      });
  }, []);

  const updateField = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const valid =
    form.brand_name.trim().length > 0 &&
    /.+@.+\..+/.test(form.email) &&
    form.website.trim().length > 0 &&
    form.category.trim().length > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) {
      toast.error("Missing information", {
        description:
          "Brand name, contact email, website, and category are required.",
      });
      return;
    }
    setLoading(true);
    try {
      const attribution = getAttribution();
      const res = await axios.post(`${API}/brand/register`, {
        brand_name: form.brand_name,
        email: form.email,
        website: form.website,
        category: form.category,
        ...attribution,
      });
      setSubmitted(true);
      setBrandCount((c) => (typeof c === "number" ? c + 1 : c));

      fireLead({
        id: res.data?.id,
        shop: `Brand · ${form.category}`,
      });

      toast.success("Brand submitted.", {
        description: "A human will read your file.",
      });
    } catch (err) {
      toast.error("Something went wrong", {
        description:
          err?.response?.data?.detail || "Please try again in a moment.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="signup"
      data-testid="beta-section"
      className="relative border-b border-line bg-white"
    >
      <div className="w-full max-w-none px-4 sm:px-8 lg:px-12 py-20 sm:py-28 lg:py-32">
        <div className="flex items-center justify-between mb-14">
          <span className="font-sans font-bold text-sm uppercase tracking-widest text-link">
            05 / Brand Intake
          </span>
          {typeof brandCount === "number" && (
            <span
              data-testid="beta-count"
              className="font-mono text-[11px] uppercase tracking-[0.28em] text-ink-soft"
            >
              {brandCount} brands in queue
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-6">
            <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-brand-orange block mb-3">
              Verified brand intake · Open
            </span>
            <h2
              data-testid="beta-headline"
              className="font-display font-bold tracking-tight text-ink text-[2rem] sm:text-[2.25rem] lg:text-[2.5rem] leading-[1.15]"
            >
              Get <span className="text-brand-orange">scannable.</span>
            </h2>

            <div className="mt-10 space-y-6 text-lg sm:text-xl text-ink-soft leading-relaxed max-w-xl">
              <p className="text-ink">
                She&apos;s in the aisle with her phone out. She scans a
                barcode. In about a second, SheScan tells her whether the
                brand in her hand is women-owned — and if it isn&apos;t, it
                shows her the ones that are.
              </p>
              <p>
                <span
                  data-testid="verified-brand-count"
                  className="text-ink font-semibold"
                >
                  {verifiedCount} brands are verified and live right now
                </span>
                , across the app and the Chrome extension. If your UPCs
                aren&apos;t in that database, you&apos;re not neutral in that
                moment. You&apos;re absent. She scans the conglomerate on the
                shelf next to you, and we hand her your competitor, because
                your competitor sent us her barcodes and you didn&apos;t.
              </p>
              <p>
                Verification is <span className="text-ink">free</span>. We
                don&apos;t sell it, we don&apos;t rank it, and no one has ever
                bought their way in. You send ownership documentation and
                your UPCs. A human reads the file. You&apos;re in, or
                you&apos;re not.
              </p>
              <p className="text-ink font-display font-bold text-2xl sm:text-3xl leading-tight pt-2">
                Send us your barcodes.
              </p>
            </div>
          </div>

          <div className="lg:col-span-6">
            <FeaturedBrandsStrip />

            {!submitted ? (
              <form
                onSubmit={submit}
                data-testid="brand-form"
                className="border border-line bg-white"
              >
                <div className="flex items-center justify-between px-6 py-3 border-b border-line">
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink">
                    Submit your brand
                  </span>
                  <span className="font-sans font-bold text-xs uppercase tracking-widest text-brand-orange">
                    Human review
                  </span>
                </div>

                <div className="p-6 sm:p-10 space-y-6">
                  <Field label="Brand / Company name" testid="field-brand-name">
                    <input
                      type="text"
                      required
                      value={form.brand_name}
                      onChange={updateField("brand_name")}
                      placeholder="e.g. Honeycomb Apothecary"
                      data-testid="input-brand-name"
                      className="w-full bg-white border border-line text-ink px-4 py-4 font-mono text-sm placeholder:text-ink-soft focus:outline-none focus:border-brand-orange focus:ring-1 focus:ring-brand-orange transition-colors"
                    />
                  </Field>

                  <Field label="Contact email" testid="field-brand-email">
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={updateField("email")}
                      placeholder="founder@yourbrand.com"
                      data-testid="input-brand-email"
                      className="w-full bg-white border border-line text-ink px-4 py-4 font-mono text-sm placeholder:text-ink-soft focus:outline-none focus:border-brand-orange focus:ring-1 focus:ring-brand-orange transition-colors"
                    />
                  </Field>

                  <Field label="Website URL" testid="field-brand-website">
                    <input
                      type="url"
                      required
                      value={form.website}
                      onChange={updateField("website")}
                      placeholder="https://yourbrand.com"
                      data-testid="input-brand-website"
                      className="w-full bg-white border border-line text-ink px-4 py-4 font-mono text-sm placeholder:text-ink-soft focus:outline-none focus:border-brand-orange focus:ring-1 focus:ring-brand-orange transition-colors"
                    />
                  </Field>

                  <Field label="Category" testid="field-brand-category">
                    <div className="relative">
                      <select
                        required
                        value={form.category}
                        onChange={updateField("category")}
                        data-testid="input-brand-category"
                        className="appearance-none w-full bg-white border border-line text-ink px-4 py-4 pr-10 font-mono text-sm focus:outline-none focus:border-brand-orange focus:ring-1 focus:ring-brand-orange transition-colors"
                      >
                        <option value="" disabled>
                          Select a category…
                        </option>
                        {BRAND_CATEGORIES.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                      <ChevronDown
                        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-soft"
                        aria-hidden
                      />
                    </div>
                  </Field>

                  <button
                    type="submit"
                    disabled={loading || !valid}
                    data-testid="brand-submit"
                    className="group w-full inline-flex items-center justify-center gap-3 border-2 border-brand-orange bg-brand-orange text-white font-sans font-bold uppercase text-sm tracking-[0.22em] px-6 py-6 hover:bg-white hover:text-ink hover:border-line transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? "Sending…" : "Send us your barcodes"}
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>

                  <p className="text-xs text-ink-soft font-mono leading-relaxed border-t border-line pt-4">
                    Free. Human review. No badge sold, no ranking, no
                    pay-to-play. You&apos;re in, or you&apos;re not.{" "}
                    <a
                      href="/privacy"
                      data-testid="brand-form-privacy-link"
                      className="link-under text-ink hover:text-brand-orange transition-colors"
                    >
                      How we handle your data.
                    </a>
                  </p>
                </div>
              </form>
            ) : (
              <div
                data-testid="brand-success"
                className="border border-line bg-white animate-fade-up"
              >
                <div className="flex items-center justify-between px-6 py-3 border-b border-line text-white bg-ink">
                  <span className="font-sans font-bold text-sm uppercase tracking-widest">
                    File received
                  </span>
                  <Check className="w-4 h-4" />
                </div>
                <div className="p-8 sm:p-12 space-y-6">
                  <h3 className="font-display font-bold text-3xl sm:text-4xl text-ink leading-[1.1]">
                    Thanks,{" "}
                    <span className="text-brand-orange">
                      {form.brand_name}.
                    </span>
                  </h3>
                  <p className="text-ink-soft text-lg leading-relaxed">
                    We&apos;ve got you. A human on our team will read your
                    ownership file and your UPCs, then reach out at{" "}
                    <span className="text-ink">{form.email}</span>. Expect a
                    decision inside 5 business days.
                  </p>
                  <div className="pt-6 border-t border-line">
                    <p className="font-sans font-bold text-sm uppercase tracking-widest text-brand-orange mb-3">
                      What happens next
                    </p>
                    <p className="text-ink-soft text-sm leading-relaxed">
                      Verified brands land in the SheScan database and start
                      surfacing the moment a shopper scans your category —
                      at the shelf, in the browser, and in the app.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, testid, children }) {
  return (
    <label data-testid={testid} className="block space-y-2">
      <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-soft block">
        {label}
      </span>
      {children}
    </label>
  );
}
